import { el, clear } from '../core/dom.js';
import { api } from '../core/api.js';
import { formatRelative } from '../core/format.js';
import { navigate } from '../core/router.js';
import { openDrawer } from './drawer.js';
import { tabs } from './tabs.js';
import { empty } from './empty.js';
import { skeleton } from './skeleton.js';
import { errorPanel } from './error.js';
import { button } from './button.js';
import {
  NOTIF_DESTINATIONS,
  appPathFromNotifDeep,
  filterNotifications,
  markAllRowsRead,
  markRowRead,
  notifGlyphKind,
  notifInitial,
  splitNotifTitle,
  unreadCount,
  visibleNotifTabs,
} from './notifications-model.js';

/**
 * @param {{ onRead?: (unread: number) => void, productId?: string, role?: string }} [opts]
 */
export function openNotifications(opts) {
  const onRead = opts && opts.onRead;
  const productId = (opts && opts.productId) || '';
  const role = opts && opts.role;
  const body = el('div', { class: 'notif' }, skeleton(4));
  openDrawer({ title: 'Notifications', body });
  load(body, { onRead, productId, role });
}

async function load(body, ctx) {
  const data = await api('/notifications');
  clear(body);
  if (!data || data.error) {
    body.append(errorPanel({
      title: 'Could not load notifications',
      message: 'The list will be here when the connection is back.',
    }));
    return;
  }
  let rows = data.notifications || [];
  let tab = 'all';
  if (ctx.onRead) ctx.onRead(data.unread != null ? data.unread : unreadCount(rows));
  const paint = () => paintList(body, rows, tab, ctx, {
    onTab: (next) => {
      tab = next;
      paint();
    },
    onMarkAll: async () => {
      const result = await api('/notifications/read-all', { method: 'POST' });
      if (result && result.error) return;
      rows = markAllRowsRead(rows);
      if (ctx.onRead) ctx.onRead(0);
      paint();
    },
    onOpen: async (row) => {
      if (!row.read_at) {
        api(`/notifications/${encodeURIComponent(row.id)}/read`, { method: 'POST' }).catch(() => {});
        rows = markRowRead(rows, row.id);
        if (ctx.onRead) ctx.onRead(unreadCount(rows));
      }
      const path = appPathFromNotifDeep(row.deep_link, ctx.productId || row.product_id);
      const drawer = body.closest('.drawer');
      const scrim = document.querySelector('.scrim');
      if (drawer) drawer.remove();
      if (scrim) scrim.remove();
      navigate(path);
    },
  });
  paint();
}

function paintList(body, rows, tab, ctx, handlers) {
  clear(body);
  const shown = filterNotifications(rows, tab);
  const unread = unreadCount(rows);
  const tabItems = visibleNotifTabs(undefined, ctx.role).map((item) => ({
    id: item.id,
    label: item.label,
    selected: tab === item.id,
  }));
  body.append(
    el('div', { class: 'notif-toolbar' },
      unread
        ? el('span', { class: 'notif-new' }, `${unread} new`)
        : el('span', { class: 'notif-new is-quiet' }, 'All caught up'),
      unread
        ? button('Mark all read', { variant: 'ghost', onClick: handlers.onMarkAll })
        : null),
    tabs(tabItems, handlers.onTab),
  );
  if (!shown.length) {
    body.append(empty({ title: 'None yet', body: 'Mentions, reports and support replies show up here.' }));
    body.append(el('p', { class: 'notif-foot' },
      'Scoped to products you can access. Mentions also email if you are away over 10 minutes.'));
    return;
  }
  body.append(el('div', { class: 'notif-rows' },
    shown.map((row) => notifRow(row, handlers.onOpen))));
  body.append(el('p', { class: 'notif-foot' },
    'Scoped to products you can access. Mentions also email if you are away over 10 minutes.'));
}

function notifRow(row, onOpen) {
  const parts = splitNotifTitle(row.title);
  const kind = notifGlyphKind(row);
  const unread = !row.read_at;
  const when = formatRelative(row.created_at);
  const dest = NOTIF_DESTINATIONS[row.class] || 'Opens dashboard';
  return el('button', {
    type: 'button',
    class: unread ? 'notif-row is-unread' : 'notif-row',
    onClick: () => onOpen(row),
  },
  glyph(kind, notifInitial(row)),
  el('span', { class: 'notif-body' },
    el('span', { class: 'notif-title' },
      el('strong', {}, parts.strong),
      parts.rest ? ` ${parts.rest}` : null),
    el('span', { class: 'notif-sub' },
      `${unread ? dest : 'Read'}${when ? ` · ${when}` : ''}`)),
  unread ? el('span', { class: 'notif-dot', 'aria-hidden': 'true' }) : null);
}

function glyph(kind, initial) {
  if (kind === 'mention') {
    return el('span', { class: 'notif-avatar', 'aria-hidden': 'true' }, initial || '@');
  }
  if (kind === 'support') {
    return el('span', { class: 'notif-square is-support', 'aria-hidden': 'true' }, 'CL');
  }
  return el('span', {
    class: kind === 'report' ? 'notif-square is-report' : 'notif-square is-connector',
    'aria-hidden': 'true',
  });
}
